import { ApolloQueryResult } from '@apollo/client';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';

import { DeliverableEvents } from '../DeliverableEvents';
import { SubmitDeliverableSubmissionModal } from '../DeliverableSubmission';
import {
  DownloadCurrentDeliverableSubmissionButton,
  DownloadCurrentDeliverableSubmissionIconButton,
  DownloadDeliverableSubmissionIconButton,
} from '../DeliverableSubmission/Download';
import { ResponsibleParties } from '../ResponsibleParties';
import { Alert } from 'packages/common-frontend/components/Alert';
import { BackButton } from 'packages/common-frontend/components/BackButton';
import { Badge } from 'packages/common-frontend/components/Badge';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { GetDeliverableQueryGQLType } from 'packages/deliverables/requests/deliverables.gql.generated';
import {
  StatusIcon,
  StatusContainer,
} from 'packages/deliverables/ui/StatusContainer';
import {
  getDeliverablesUrl,
  isCompletedDeliverableStatus,
  isDueDateOverdue,
} from 'packages/deliverables/ui/utils';
import { FiUpload } from 'react-icons/fi';

type Props = {
  deliverable: GetDeliverableQueryGQLType;
  requestingOrganizationId: string;
  refetch: (
    variables: { deliverableId: string } | undefined,
  ) => Promise<ApolloQueryResult<GetDeliverableQueryGQLType>>;
};
export const DeliverableDetailResponsibleParty = ({
  deliverable,
  requestingOrganizationId,
  refetch,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [isSubmissionOpen, setIsSubmissionOpen] = useState<boolean>(false);

  const {
    deliverable: {
      submissions,
      events,
      dependencies,
      currentSubmission,
      responsibleParties,
      isDeliverableResponsibleParty,
      ...currentDeliverable
    },
  } = deliverable;

  const {
    id: deliverableId,
    name,
    description,
    dueDate,
    status,
    creditFacilityName,
    mostRecentVersion,
    expectedValueConfig,
  } = currentDeliverable;

  const isBlockedByDependencies = _.some(
    dependencies,
    ({ deliverable: deliverableDependency }) => {
      return !isCompletedDeliverableStatus(deliverableDependency.status);
    },
  );
  const isBlockedByVersion = !mostRecentVersion.isMostRecentDeliverableVersion;

  const deliverablesUrl = getDeliverablesUrl(location.pathname);

  const noSubmissionRequired = expectedValueConfig.valueType === 'noSubmission';

  if (!isDeliverableResponsibleParty) {
    errorToastConfig.title = `You do not have permission to view this deliverable`;
    toast(errorToastConfig);
  }

  // Use this variable for errors that bubble up to the component level route
  // This ensures we only toast the error once when there are multiple errors
  // We should use Chakra's toast.isActive, or a react hook in general, but the state re-renders when there are multiple errors in a given error boundary
  let routeLevelError = false;
  return (
    <Box className="main">
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load deliverables page`;
            routeLevelError = true;
            toast(errorToastConfig);
            navigate('/deliverables');
          }
        }}
      >
        <Box>
          <Flex justify="space-between" width="100%" mb="35px">
            <Heading as="h1" fontSize="3xl">
              {name}
            </Heading>

            <Box textAlign="right">
              <BackButton />
            </Box>
          </Flex>
          <HStack spacing={5} alignItems="start" mb="35px">
            <Heading as="h2" fontSize="xl" mb="10px" fontWeight="normal">
              Status
            </Heading>
            <StatusContainer
              canUpdate={!isBlockedByVersion}
              deliverable={currentDeliverable}
              onUpdate={() => refetch({ deliverableId })}
            />
          </HStack>
          <Heading
            as="h2"
            fontSize="xl"
            mb="16px"
            mt="16px"
            fontWeight="normal"
          >
            Due Date
          </Heading>
          <Text as="p" mb="35px">
            {commonDate.fullDateStrToHumanDateString(dueDate)}
          </Text>
          <Heading
            as="h2"
            fontSize="xl"
            mb="16px"
            mt="16px"
            fontWeight="normal"
          >
            Credit Facility
          </Heading>
          <Text mb="35px">{creditFacilityName}</Text>
          <Heading
            as="h2"
            fontSize="xl"
            mb="16px"
            mt="16px"
            fontWeight="normal"
          >
            Description
          </Heading>
          <Text mb="35px">{description}</Text>
          <Heading
            as="h2"
            fontSize="xl"
            mb="16px"
            mt="16px"
            fontWeight="normal"
          >
            Responsible Parties
          </Heading>
          <ResponsibleParties
            responsibleParties={responsibleParties}
            requestingOrganizationId={requestingOrganizationId}
          />
          {!noSubmissionRequired && (
            <>
              <HStack alignItems="center">
                <Text fontSize="xl" mb="16px" mt="16px" fontWeight="normal">
                  Submissions
                </Text>
                <SubmitDeliverableSubmissionModal
                  deliverable={currentDeliverable}
                  isOpen={isSubmissionOpen}
                  setIsOpen={setIsSubmissionOpen}
                  onCompleteSubmit={() => refetch({ deliverableId })}
                  submissions={submissions}
                />
              </HStack>
              {isBlockedByVersion && (
                <>
                  {mostRecentVersion.mostRecentDeliverableId && (
                    <Button
                      variant="solid"
                      colorScheme="green"
                      onClick={() =>
                        navigate(
                          deliverablesUrl
                            ? `${deliverablesUrl}/${mostRecentVersion.mostRecentDeliverableId}`
                            : `deliverables/
                      ${mostRecentVersion.mostRecentDeliverableId}`,
                        )
                      }
                    >
                      See New Version
                    </Button>
                  )}
                  <Alert
                    status="warning"
                    mt="10px"
                    mb="10px"
                    variant="subtle"
                    isClosable={false}
                  >
                    <Text>
                      A new version of this file is available. This version is
                      no longer valid.
                    </Text>
                  </Alert>
                </>
              )}
              {!isBlockedByVersion &&
                isBlockedByDependencies &&
                !isCompletedDeliverableStatus(status) && (
                  <Alert
                    status="error"
                    mb="35px"
                    variant="subtle"
                    isClosable={false}
                  >
                    <Text>
                      Submission of this deliverable is currently blocked by a
                      dependency
                    </Text>
                  </Alert>
                )}
              {_.isEmpty(submissions) && isCompletedDeliverableStatus(status) && (
                <Alert
                  status="info"
                  mt="10px"
                  mb="35px"
                  variant="subtle"
                  isClosable={false}
                >
                  <Text>
                    No submissions have been received for this deliverable.
                  </Text>
                </Alert>
              )}
              {!_.isEmpty(submissions) ? (
                <>
                  {!currentSubmission && (
                    <>
                      <Button
                        rightIcon={<FiUpload />}
                        colorScheme="green"
                        variant="solid"
                        onClick={() => setIsSubmissionOpen(true)}
                      >
                        {`Submit ${name}`}
                      </Button>

                      {!isCompletedDeliverableStatus(status) &&
                        isDueDateOverdue(dueDate) && (
                          <Alert
                            status="error"
                            variant="subtle"
                            isClosable={false}
                          >
                            <Text>
                              This deliverable is overdue. Submit immediately.
                            </Text>
                          </Alert>
                        )}
                    </>
                  )}
                  {currentSubmission?.__typename ===
                    'DeliverableFileSubmission' && (
                    <>
                      <HStack spacing={4}>
                        <DownloadCurrentDeliverableSubmissionButton
                          deliverableId={deliverableId}
                          fileName={currentSubmission.fileName}
                          colorScheme="green"
                        />

                        <Button
                          rightIcon={<FiUpload />}
                          colorScheme="green"
                          variant="solid"
                          onClick={() => setIsSubmissionOpen(true)}
                        >
                          {`Submit ${name}`}
                        </Button>
                      </HStack>

                      <Alert
                        status="success"
                        variant="subtle"
                        isClosable={false}
                      >
                        <Text>
                          The submission has been successfully received and
                          processed.
                        </Text>
                      </Alert>
                    </>
                  )}
                  <Accordion allowToggle mb="35px">
                    <AccordionItem border="none">
                      <AccordionButton>
                        <AccordionIcon />
                        Submission History
                      </AccordionButton>
                      <AccordionPanel>
                        <Table mb="35px">
                          <Thead>
                            <Tr>
                              <Th>Date</Th>
                              <Th>Value</Th>
                              <Th />
                            </Tr>
                          </Thead>
                          <Tbody>
                            {_.map(submissions, (submission) => {
                              const {
                                __typename,
                                created_at: createdAt,
                                id: submissionId,
                                // note,
                                value,
                              } = submission;
                              return (
                                <Tr key={submissionId}>
                                  <Td>
                                    {commonDate.epochToLocaleDateString(
                                      createdAt,
                                    )}
                                  </Td>
                                  <Td>
                                    {__typename === 'DeliverableFileSubmission'
                                      ? submission.fileName
                                      : value}
                                  </Td>
                                  <Td>
                                    {__typename ===
                                    'DeliverableFileSubmission' ? (
                                      <DownloadDeliverableSubmissionIconButton
                                        deliverableId={deliverableId}
                                        deliverableSubmissionId={submissionId}
                                        fileName={submission.fileName}
                                      />
                                    ) : null}
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </>
              ) : (
                !isBlockedByDependencies &&
                !isBlockedByVersion && (
                  <>
                    <Button
                      rightIcon={<FiUpload />}
                      colorScheme="green"
                      variant="solid"
                      onClick={() => setIsSubmissionOpen(true)}
                    >
                      {`Submit ${name}`}
                    </Button>

                    {!isCompletedDeliverableStatus(status) &&
                      isDueDateOverdue(dueDate) && (
                        <Alert
                          status="error"
                          mt="10px"
                          mb="35px"
                          variant="subtle"
                          isClosable={false}
                        >
                          <Text>
                            This deliverable is overdue. Submit immediately.
                          </Text>
                        </Alert>
                      )}
                  </>
                )
              )}
            </>
          )}
          {!_.isEmpty(dependencies) && (
            <Box mb="35px" mt="16px">
              <Heading as="h2" fontSize="xl" mb="10px" fontWeight="normal">
                Dependencies
              </Heading>
              <Table borderRadius="lg">
                <Thead>
                  <Tr>
                    <Th textAlign="center" w="10%">
                      Status
                    </Th>
                    <Th>Name</Th>
                    <Th textAlign="center">Value</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {_.map(
                    dependencies,
                    ({
                      deliverable: {
                        id: dependencyId,
                        name: dependencyName,
                        status: dependencyStatus,
                        currentSubmission: dependencyCurrentSubmission,
                      },
                    }) => {
                      return (
                        <Tr key={dependencyId}>
                          <Td textAlign="center">
                            <Center>
                              <StatusIcon status={dependencyStatus} />
                            </Center>
                          </Td>
                          <Td>{dependencyName}</Td>
                          <Td textAlign="center">
                            {dependencyCurrentSubmission &&
                              (dependencyCurrentSubmission?.__typename ===
                              'DeliverableFileSubmission' ? (
                                <DownloadCurrentDeliverableSubmissionIconButton
                                  deliverableId={dependencyId}
                                  fileName={
                                    dependencyCurrentSubmission.fileName
                                  }
                                />
                              ) : (
                                <Text>
                                  {String(dependencyCurrentSubmission.value)}
                                </Text>
                              ))}
                            {!dependencyCurrentSubmission &&
                              dependencyStatus === 'waived' && (
                                <Center>
                                  <Badge
                                    colorScheme="gray"
                                    maxWidth="160px"
                                    borderRadius="lg"
                                    p="10px"
                                  >
                                    Dependency Waived
                                  </Badge>
                                </Center>
                              )}
                            {!dependencyCurrentSubmission &&
                              dependencyStatus !== 'waived' &&
                              '-'}
                          </Td>
                        </Tr>
                      );
                    },
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
          <Box mb="35px">
            <Heading
              as="h2"
              fontSize="xl"
              mb="10px"
              mt="36px"
              fontWeight="normal"
            >
              History
            </Heading>
            {!_.isEmpty(events) ? (
              <DeliverableEvents events={events} />
            ) : (
              <Alert isClosable={false} status="info" variant="subtle">
                <Text>No events to display</Text>
              </Alert>
            )}
          </Box>
          {/* Misc metadata */}
          <Heading as="h2" fontSize="xl" mb="10px" fontWeight="normal">
            Deliverable ID
          </Heading>
          <Text as="p" mb="35px">
            {deliverableId}
          </Text>
        </Box>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
